/**
 * Created: 2026-04-07
 * Initial commit: Add court category and create-event dialog (feat/event/mr-9)
 *
 * Dialog for creating or editing an event.
 * - Create mode: slots prop provided, no editEvent prop
 * - Edit mode: editEvent prop provided, slots is empty
 */

import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import {
  Field,
  FileField,
  type ISchema,
  message,
  NumberField,
  useForm,
} from '@homemaster/jslib'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { GAME_FORMAT_LABEL } from '@tc-app/shared/labels'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import {
  CreateEventDocument,
  GameFormat,
  type GetEventQuery,
  UpdateEventDocument,
} from '../gql/graphql'
import NumberInput from './NumberInput'

// Created: 2026-04-07 | Initial commit: Add court category and create-event dialog
gql`
  mutation CreateEvent($eventInput: CreateEventInput!) {
    createEvent(eventInput: $eventInput) {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

// Created: 2026-04-18 | Initial commit: Add updateEvent mutation (feat/event/mr-20)
gql`
  mutation UpdateEvent($eventId: UUID!, $eventInput: UpdateEventInput!) {
    updateEvent(eventId: $eventId, eventInput: $eventInput) {
      ... on Event {
        id
        name
        location
        description
        signupCode
        drawSize
        fee
        minRating
        maxRating
        enforceRating
        numSignups
        coverImages
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

type EventFormValues = {
  name: string
  location: string
  description: string
  signupCode: string
  fee: number
  minRating: number
  maxRating: number
  enforceRating: boolean
  drawSize: number
  coverImages: string[]
}

/** Allowed `drawSize` values for a given game format, or null if unrestricted. */
function allowedDrawSizes(gameFormat: GameFormat): number[] | null {
  switch (gameFormat) {
    case GameFormat.Singles:
      return [3, 4, 5, 6, 8]
    case GameFormat.Doubles:
      return [2, 4, 6, 8, 10, 12, 14, 16]
    case GameFormat.RotatingDoubles:
      return [4, 5]
    default:
      return null
  }
}

/** Error message shown when `drawSize` is outside `allowedDrawSizes(gameFormat)`. */
function drawSizeAllowedMessage(gameFormat: GameFormat): string {
  switch (gameFormat) {
    case GameFormat.Singles:
      return '单打签位只能为3、4、5、6 或 8'
    case GameFormat.Doubles:
      return '双打签位必须为偶数'
    default:
      return '循环双打签位只能为4或5'
  }
}

type EventFragment = Extract<GetEventQuery['event'], { __typename: 'Event' }>

interface EventFormDialogProps {
  slots: { courtId: string; courtName: string; startTime: string }[]
  /** When provided, the dialog operates in edit mode for this event. */
  editEvent?: EventFragment | null
  onClose: () => void
  onSuccess: () => void
}

/** Cell-style row: label on the left, input on the right. */
function CellRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {label}
        </Typography>
        {children}
      </Box>
    </Box>
  )
}

export default function EventFormDialog({
  slots,
  editEvent,
  onClose,
  onSuccess,
}: EventFormDialogProps) {
  const isEditMode = !!editEvent
  const [gameFormat, setGameFormat] = useState<GameFormat>(
    editEvent?.gameFormat ?? GameFormat.Freeplay,
  )

  const schema = useMemo((): ISchema => {
    const drawSizeRules: string[] = ['min:2', 'max:16']
    const allowed = allowedDrawSizes(gameFormat)
    if (allowed) drawSizeRules.push(`in:${allowed.join(',')}`)
    return {
      rules: {
        name: 'required|max:40',
        location: 'required|max:50',
        fee: 'required',
        drawSize: drawSizeRules,
        signupCode: 'digits:6',
      },
      messages: {
        'required.name': '请填写活动名称',
        'required.location': '请填写活动地点',
        'required.fee': '请填写报名费',
        'in.drawSize': drawSizeAllowedMessage(gameFormat),
        'min.drawSize': '签位须在2-16 之间',
        'max.drawSize': '签位须在2-16 之间',
        'max:name': '活动名称不能超过40字',
        'max:location': '活动地点不能超过50字',
        'digits.signupCode': '报名口令必须是6位数字',
      },
    }
  }, [gameFormat])

  const initialLocation = useMemo(() => {
    if (isEditMode) return editEvent?.location ?? ''
    const seen = new Set<string>()
    return slots
      .map((s) => s.courtName)
      .filter((name) => name && !seen.has(name) && seen.add(name))
      .join(' · ')
  }, [slots, isEditMode, editEvent])

  const courtName = useMemo(
    () => (isEditMode ? null : initialLocation),
    [isEditMode, initialLocation],
  )

  const form = useForm<EventFormValues>(
    {
      name: editEvent?.name ?? '',
      location: initialLocation,
      description: editEvent?.description ?? '',
      signupCode: editEvent?.signupCode ?? '',
      fee: editEvent?.fee ? Number(editEvent.fee) : 50,
      minRating: editEvent?.minRating ?? 2.5,
      maxRating: editEvent?.maxRating ?? 3.5,
      enforceRating: editEvent?.enforceRating ?? false,
      drawSize: editEvent?.drawSize ?? 4,
      coverImages: editEvent?.coverImages ?? [],
    },
    schema,
  )

  const [showSignupCode, setShowSignupCode] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const [createEvent] = useMutation(CreateEventDocument)
  const [updateEvent] = useMutation(UpdateEventDocument)

  // Derived from slots (create mode only)
  const startTime = useMemo(() => {
    if (slots.length === 0) return null
    return slots.reduce(
      (min, s) => (s.startTime < min ? s.startTime : min),
      slots[0].startTime,
    )
  }, [slots])

  const duration = useMemo(() => new Set(slots.map((s) => s.startTime)).size, [slots])

  const startTimeFormatted = startTime
    ? format(new Date(startTime), 'yyyy-MM-dd HH:mm')
    : ''
  const endTimeFormatted = startTime
    ? format(new Date(new Date(startTime).getTime() + duration * 3600_000), 'HH:mm')
    : ''

  const handleClose = () => {
    form.reset()
    setGameFormat(GameFormat.Freeplay)
    setShowSignupCode(false)
    setSubmitError('')
    onClose()
  }

  const handleSubmit = async () => {
    if (form.validate(false)) return

    setSubmitting(true)
    try {
      const {
        name,
        location,
        description,
        signupCode,
        fee,
        minRating,
        maxRating,
        enforceRating,
        drawSize,
        coverImages,
      } = form.values

      const commonFields = {
        name: (name ?? '').trim(),
        location: (location ?? '').trim() || null,
        description: (description ?? '').trim() || null,
        signupCode: (signupCode ?? '').trim() || null,
        drawSize: drawSize ?? 8,
        minRating,
        maxRating,
        enforceRating,
        fee,
        coverImages: coverImages.length > 0 ? coverImages : null,
      }

      if (isEditMode) {
        const result = await updateEvent({
          variables: {
            eventId: editEvent?.id,
            eventInput: commonFields,
          },
        })
        const data = result.data?.updateEvent
        if (data?.__typename === 'GraphqlRequestError') {
          setSubmitError(data.message ?? '')
          return
        }
        message.success('活动已保存', { autoHide: 2000 })
      } else {
        const result = await createEvent({
          variables: {
            eventInput: {
              ...commonFields,
              gameFormat,
              slots: slots.map((s) => ({ courtId: s.courtId, startTime: s.startTime })),
            },
          },
        })
        const data = result.data?.createEvent
        if (data?.__typename === 'GraphqlRequestError') {
          setSubmitError(data.message ?? '')
          return
        }
        message.success('活动已创建', { autoHide: 2000 })
      }

      onSuccess()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {isEditMode ? '编辑活动' : '创建活动'}
        {courtName && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {courtName} · {startTimeFormatted}–{endTimeFormatted}（{duration} 小时）
          </Typography>
        )}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1}>
          {!isEditMode && (
            <FormControl>
              <FormLabel>赛制</FormLabel>
              <RadioGroup
                row
                value={gameFormat}
                onChange={(e) => {
                  setGameFormat(e.target.value as GameFormat)
                  form.validate(false)
                }}
              >
                <FormControlLabel
                  value={GameFormat.Freeplay}
                  control={<Radio />}
                  label={GAME_FORMAT_LABEL[GameFormat.Freeplay]}
                />
                <FormControlLabel
                  value={GameFormat.Singles}
                  control={<Radio />}
                  label={GAME_FORMAT_LABEL[GameFormat.Singles]}
                />
                <FormControlLabel
                  value={GameFormat.Doubles}
                  control={<Radio />}
                  label={GAME_FORMAT_LABEL[GameFormat.Doubles]}
                />
                <FormControlLabel
                  value={GameFormat.RotatingDoubles}
                  control={<Radio />}
                  label={GAME_FORMAT_LABEL[GameFormat.RotatingDoubles]}
                />
              </RadioGroup>
            </FormControl>
          )}

          <Field
            form={form}
            name="name"
            label="活动名称"
            required
            fullWidth
            helperText={`${(form.values.name ?? '').length}/40`}
          />

          <Field
            form={form}
            name="location"
            label="活动地点"
            required
            fullWidth
            helperText={`${(form.values.location ?? '').length}/50`}
          />

          <CellRow label="签位 *">
            <Field
              form={form}
              name="drawSize"
              sx={{ width: 150 }}
              component={NumberField}
              componentProps={{
                min: isEditMode ? (editEvent?.numSignups ?? 2) : 2,
                max: 16,
                precision: 0,
              }}
            />
          </CellRow>

          <CellRow label="等级范围 *">
            <Box sx={{ width: 300 }}>
              <Slider
                value={[form.values.minRating, form.values.maxRating]}
                onChange={(_, val) => {
                  const [min, max] = val as [number, number]
                  form.update({ minRating: min, maxRating: max })
                }}
                min={1}
                max={7}
                step={0.1}
                disableSwap
                valueLabelDisplay="on"
                disabled={isEditMode && (editEvent?.numSignups ?? 0) > 0}
                sx={{ mt: 4 }}
              />
            </Box>
          </CellRow>

          <CellRow label="限制报名等级">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                {form.values.enforceRating ? '限制' : '不限'}
              </Typography>
              <Switch
                checked={form.values.enforceRating}
                onChange={(e) => form.update({ enforceRating: e.target.checked })}
                disabled={isEditMode && (editEvent?.numSignups ?? 0) > 0}
              />
            </Box>
          </CellRow>

          <CellRow label="报名费（元）*">
            <Field
              form={form}
              name="fee"
              sx={{ width: 150 }}
              disabled={isEditMode && (editEvent?.numSignups ?? 0) > 0}
              slotProps={{
                input: {
                  inputComponent: NumberInput,
                  inputProps: { max: 999, decimalScale: 2, fixedDecimalScale: true },
                },
              }}
            />
          </CellRow>

          <Field
            form={form}
            name="description"
            label="活动说明（可选）"
            required={false}
            fullWidth
            multiline
            rows={3}
            helperText={`${(form.values.description ?? '').length}/200`}
          />

          <CellRow label="报名口令">
            <Field
              form={form}
              name="signupCode"
              sx={{ width: 150 }}
              required={false}
              placeholder="6位数字"
              type={showSignupCode ? 'text' : 'password'}
              inputMask={/^[0-9]{0,6}$/}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showSignupCode ? '隐藏报名口令' : '显示报名口令'}
                        onClick={() => setShowSignupCode((show) => !show)}
                        edge="end"
                        size="small"
                      >
                        {showSignupCode ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </CellRow>

          <Field
            component={FileField}
            form={form}
            name="coverImages"
            label="活动封面"
            required={false}
          />

          {submitError && (
            <Typography color="error" variant="body2">
              {submitError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {isEditMode ? '保存' : '提交'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
